import React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Skeleton } from 'components/ui/skeleton';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { useSmoothLoading } from 'lib/use-smooth-loading';

interface LhcTableProps {
  data: Record<string, any>[];
  loading: boolean;
  hasLoadedOnce: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  onRowClick?: (row: Record<string, any>) => void;
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const LhcTable: React.FC<LhcTableProps> = ({
  data,
  loading,
  hasLoadedOnce,
  sortBy,
  sortDir,
  onSort,
  onRowClick,
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const { isLoading, showContent } = useSmoothLoading(loading, {
    minLoadingTime: 150,
    debounceTime: 100,
  });

  const rawHeaders = Array.from(new Set(data.flatMap(row => Object.keys(row))));

  // Top Routes (summary) table: force Avg Value to sit before Min/Max.
  // Detail rows don't have avg_value, so they keep their natural order.
  const isVendor = rawHeaders.includes('vendor_name') && rawHeaders.includes('concentration_percent');
  const isSummary = !isVendor && rawHeaders.includes('avg_value') && rawHeaders.includes('min_value') && rawHeaders.includes('max_value');
  const SUMMARY_ORDER = ['origin', 'destination', 'vehicle_type', 'count', 'total_value', 'avg_value', 'min_value', 'max_value'];
  const VENDOR_ORDER = ['vendor_name', 'vendor_type', 'lhc_count', 'total_value', 'avg_value', 'min_value', 'max_value', 'concentration_percent'];
  const columnOrder = isSummary ? SUMMARY_ORDER : isVendor ? VENDOR_ORDER : null;
  const headers = columnOrder
    ? [...rawHeaders].sort((a, b) => {
        const ia = columnOrder.indexOf(a);
        const ib = columnOrder.indexOf(b);
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      }).filter(h => columnOrder.includes(h))
    : rawHeaders;

  const formatValue = (val: any, header?: string) => {
    if (val === null || val === undefined || val === '') return '-';

    const h = header?.toLowerCase() || '';

    if (h.includes('date')) {
      const date = new Date(val);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
      return val;
    }

    if (typeof val === 'number' || (!isNaN(Number(val)) && val !== '')) {
      const num = Number(val);
      let formatted = num.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      if (h === 'concentration_percent') {
        return `${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
      }

      if (['total_value', 'min_value', 'max_value', 'avg_value', 'lorry_hire_expenses', 'rkm', 'rtkm'].includes(h)) {
        if (num >= 10000000) formatted = `₹ ${(num / 10000000).toFixed(2)} Cr`;
        else if (num >= 100000) formatted = `₹ ${(num / 100000).toFixed(2)} L`;
        else formatted = `₹ ${formatted}`;
      } else if (h.includes('distance')) {
        formatted = `${formatted} km`;
      } else if (['actual_weight', 'charged_weight', 'vehicle_capacity', 'vehicle_loading_capacity'].includes(h)) {
        formatted = num >= 1000 ? `${(num / 1000).toFixed(2)} tonne` : `${formatted} kg`;
      } else if (h.includes('utilization')) {
        formatted = `${formatted}%`;
      }
      return formatted;
    }
    return String(val);
  };

  const formatHeader = (header: string) => {
    const h = header.toLowerCase();

    if (h === 'rkm') return 'Rate per km';
    if (h === 'rtkm') return 'Rate per tonne per km';
    if (h === 'concentration_percent') return 'Concentration %';
    if (h === 'lhc_count') return 'LHC Count';
    if (h === 'vendor_name') return 'Vendor Name';
    if (h === 'vendor_type') return 'Vendor Type';

    return header
      .replace(/_/g, ' ')
      .replace(/\w\S*/g, txt => {
        const lower = txt.toLowerCase();
        if (lower === 'lhc') return 'LHC';

        return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
      });
  };

  const NUMERIC_KEYS = new Set([
    'total_value', 'min_value', 'max_value', 'avg_value', 'lorry_hire_expenses',
    'rkm', 'rtkm', 'actual_weight', 'charged_weight', 'vehicle_capacity',
    'vehicle_loading_capacity', 'count', 'total_count', 'min_count', 'max_count', 'avg_count',
    'lhc_count', 'concentration_percent',
  ]);

  const isNumericHeader = (header: string) => {
    const h = header.toLowerCase();
    return NUMERIC_KEYS.has(h) || h.includes('distance') || h.includes('utilization');
  };

  // The Avg Value (median quote rate) column is highlighted light-blue, matching Monthly P&L.
  const isHighlightHeader = (header: string) => header.toLowerCase() === 'avg_value';

  // Origin / Destination shown in bold (like the P&L description column).
  const isBoldHeader = (header: string) => ['origin', 'destination', 'vendor_name'].includes(header.toLowerCase());

  const isSortableHeader = (header: string) => header.toLowerCase() !== 'vendor_type';

  const renderSkeletonRows = () => {
    const skeletonCols = headers.length || 8;

    return Array.from({ length: 12 }).map((_, rowIdx) => (
      <tr key={`skeleton-${rowIdx}`}>
        {Array.from({ length: skeletonCols }).map((_, colIdx) => (
          <td key={colIdx} className="border border-gray-200 px-2.5 py-2">
            <Skeleton className="h-4 w-full" />
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <div className="flex flex-col">
      <div className="rounded-lg border border-gray-200 bg-card overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-100">
              {headers.map(header => {
                const active = sortBy === header;
                const numeric = isNumericHeader(header);
                const sortable = isSortableHeader(header);

                return (
                  <th
                    key={header}
                    onClick={sortable ? () => onSort?.(header) : undefined}
                    className={cn(
                      'border border-gray-300 px-2.5 py-2 whitespace-nowrap font-bold text-gray-700 select-none',
                      sortable && 'cursor-pointer transition-colors hover:text-gray-900'
                    )}
                  >
                    <div className={cn('flex items-center gap-1', numeric ? 'justify-end' : 'justify-start')}>
                      {formatHeader(header)}

                      {sortable && (
                        active ? (
                          sortDir === 'asc' ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )
                        ) : (
                          <ChevronsUpDown size={14} className="text-gray-400" />
                        )
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {(loading || isLoading || !hasLoadedOnce) ? (
              renderSkeletonRows()
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={headers.length || 1} className="text-center py-16 text-muted-foreground">
                  No data available
                </td>
              </tr>
            ) : (
              data.map((row, idx) => (
                <tr
                  key={idx}
                  onClick={() => onRowClick?.(row)}
                  className={cn(
                    `transition-opacity duration-300 hover:bg-gray-50 ${showContent ? 'opacity-100' : 'opacity-0'}`,
                    onRowClick ? 'cursor-pointer' : 'cursor-default'
                  )}
                >
                  {headers.map(key => (
                    <td
                      key={key}
                      className={cn(
                        'border border-gray-200 px-2.5 py-1.5 text-sm text-foreground whitespace-nowrap tabular-nums',
                        isNumericHeader(key) ? 'text-right' : 'text-left',
                        isBoldHeader(key) && 'font-bold',
                        isHighlightHeader(key) && 'bg-blue-50 font-semibold'
                      )}
                    >
                      {formatValue(row[key], key)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
        <span className="text-sm text-muted-foreground">
          Showing{' '}
          <strong className="text-foreground">{totalItems === 0 ? 0 : (page - 1) * pageSize + 1}</strong>{' '}
          to <strong className="text-foreground">{Math.min(page * pageSize, totalItems)}</strong> of{' '}
          <strong className="text-foreground">{totalItems}</strong>
        </span>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            Rows per page
            <select
              value={pageSize}
              onChange={e => onPageSizeChange(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
            >
              {[10, 20, 50, 100].map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page === 1}
              onClick={() => onPageChange(1)}
            >
              <ChevronsLeft size={16} />
            </Button>

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page === 1}
              onClick={() => onPageChange(page - 1)}
            >
              <ChevronLeft size={16} />
            </Button>

            <span className="px-2 text-sm text-muted-foreground">
              Page <strong className="text-foreground">{page}</strong> / {totalPages}
            </span>

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page === totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              <ChevronRight size={16} />
            </Button>

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page === totalPages}
              onClick={() => onPageChange(totalPages)}
            >
              <ChevronsRight size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LhcTable;
